import { CheckBox, Message } from '@mui/icons-material';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { FC, useEffect, useState } from 'react';
import { Menu, MenuItemLink, MenuProps } from 'react-admin';
import { useLocation } from 'react-router-dom';
import CustomMenuItemLink from '../components/common/CustomMenu/CustomMenuItemLink';
import SubMenu from '../components/common/CustomMenu/SubMenu';
import { REFERRALS_HUB_PATH } from '../components/referrals/hub/referralHubPaths';
import featuresClient, { FeatureFlags, FeaturesName } from '../lib/features';
import { AuthService } from '../services';
import MixpanelService from '../services/mixpanel';
import { Roles } from '../util/constants';
import { MENU_ITEMS } from './menuItems';

export const CustomMenu: FC<MenuProps> = ({ dense = false }) => {
  const { pathname } = useLocation();
  const [openSubmenus, setOpenSubmenus] = useState<{ [key: string]: boolean }>({});
  const [features, setFeatures] = useState<FeatureFlags>({
    patientLabResults: false,
    medicalSetup: false,
    patientConditions: false,
    auditLogs: false,
    communicationsCenter: true,
    documents: false,
    coupons: false,
    referralsCreation: false,
    referralsHub: false,
  });

  useEffect(() => {
    const newOpenState: { [key: string]: boolean } = {};

    Object.entries(MENU_ITEMS).forEach(([menuId, menuItem]) => {
      const paths = [menuItem.path, ...(menuItem.children?.map((child) => child.path) || [])];
      newOpenState[menuId] = paths.some((path) => pathname.startsWith(path));
    });

    setOpenSubmenus(newOpenState);
  }, [pathname]);

  useEffect(() => {
    const loadFeatures = async () => {
      try {
        const [
          communicationsCenter,
          documents,
          coupons,
          referralsCreation,
          referralsHub,
        ] = await featuresClient.fetchFeatureFlagsStatus([
          { name: FeaturesName.OpsDashShowCommunicationsCenter },
          { name: FeaturesName.OpsDashShowDocuments },
          { name: FeaturesName.OpsDashShowCoupons },
          { name: FeaturesName.OpsDashShowReferralsCreation },
          { name: FeaturesName.OpsDashShowReferralsHub },
        ]);

        setFeatures((prev) => ({
          ...prev,
          communicationsCenter,
          documents,
          coupons,
          referralsCreation,
          referralsHub,
        }));
      } catch (error) {
        console.error('Failed to load feature flags:', error);
      }
    };

    loadFeatures();
  }, []);

  const handleToggle = (menuId: string) => {
    setOpenSubmenus((prev) => ({
      ...prev,
      [menuId]: !prev[menuId],
    }));
  };

  const trackMenuClick = (field: string) => {
    MixpanelService.trackButtonClicked({
      Field: field,
      source: pathname,
      Type: 'link',
    });
  };

  const hasBillingRole = AuthService.checkRoleAccess(Roles.Billing);
  const hasOperationsRole = AuthService.checkRoleAccess(Roles.Operations);

  return (
    <Menu dense={dense} className='menu-overflow-hidden'>
      {/* Billing Section */}
      {hasBillingRole && (
        <>
          <MenuItemLink
            to='/eligibility'
            primaryText='Eligibility'
            leftIcon={<CheckBox />}
            onClick={() => trackMenuClick('Eligibility')}
          />

        </>
      )}

      {/* Multi-level Menus */}
      {Object.entries(MENU_ITEMS).map(([menuId, menuItem]) => {
        return (
          <SubMenu
            key={menuId}
            handleToggle={() => handleToggle(menuId)}
            isOpen={openSubmenus[menuId] || false}
            name={menuItem.label}
            icon={menuItem.icon}
            dense={dense}
            menuPath={menuItem.path}
            childPaths={menuItem.children?.map((child) => child.path) || []}>
            {menuItem.children?.map((child) => {
              if (
                child.id === 'referrals-create' &&
                (!hasOperationsRole || !features.referralsCreation)
              ) {
                return null;
              }

              return (
                <CustomMenuItemLink
                  key={child.id}
                  to={child.path}
                  primaryText={child.label}
                  leftIcon={child.icon}
                  onClick={() => trackMenuClick(child.label)}
                />
              );
            })}
          </SubMenu>
        );
      })}

      {/* Feature Flag Controlled Items */}

      {hasOperationsRole && features.referralsHub && (
        <MenuItemLink
          to={REFERRALS_HUB_PATH}
          primaryText='Referrals'
          leftIcon={<AssignmentIndIcon />}
          onClick={() => trackMenuClick('Referrals')}
        />
      )}

      {hasOperationsRole && features.communicationsCenter && (
        <MenuItemLink
          to='/communications'
          primaryText='Comms Center'
          leftIcon={<Message />}
          onClick={() => trackMenuClick('Comms Center')}
        />
      )}

      {hasOperationsRole && features.coupons && (
        <MenuItemLink
          to='/coupons'
          primaryText='Coupons'
          leftIcon={<LocalOfferIcon />}
          onClick={() => trackMenuClick('Coupons')}
        />
      )}
    </Menu>
  );
};
